import { useEffect, useState } from 'react';
import {
  suportaAvisos,
  permissaoNegada,
  precisaInstalarNoIphone,
  ligarAvisos,
  desligarAvisos,
  situacaoAvisos,
  estaLigadoPara,
  type PapelAviso,
} from '@/lib/avisos';
import { mostrarToast } from '@/lib/formularioGuiado';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import FolhaColocarNaTela from './FolhaColocarNaTela';

type EstadoAviso = 'verificando' | 'normal' | 'ligando' | 'ligado' | 'bloqueado';

interface BotaoAvisosProps {
  eventoId: number;
  carteiraCodigo?: string | null;
  papel?: PapelAviso;
  rotulo: string;
  toastSucesso?: string;
  className?: string;
}

const TEXTO_BLOQUEADO =
  'Os avisos estão bloqueados neste navegador. Para ligar: ⓘ ao lado do endereço › Notificações › Permitir.';

const TEXTO_SUCESSO_PADRAO =
  'Pronto! Você recebe um aviso quando o Pix confirmar e quando as fichas abrirem.';

export default function BotaoAvisos({
  eventoId,
  carteiraCodigo = null,
  papel = 'comprador',
  rotulo,
  toastSucesso = TEXTO_SUCESSO_PADRAO,
  className = '',
}: BotaoAvisosProps) {
  const [estado, setEstado] = useState<EstadoAviso>('verificando');
  const [confirmandoDesligar, setConfirmandoDesligar] = useState(false);
  const [instalarAberto, setInstalarAberto] = useState(false);

  // Ao abrir: descobre se já está ligado (sem pedir permissão).
  useEffect(() => {
    if (!suportaAvisos()) return;
    if (permissaoNegada()) {
      setEstado('bloqueado');
      return;
    }
    let ativo = true;
    situacaoAvisos().then((sit) => {
      if (!ativo) return;
      setEstado(estaLigadoPara(sit, eventoId) ? 'ligado' : 'normal');
    });
    return () => {
      ativo = false;
    };
  }, [eventoId]);

  async function ligar() {
    if (precisaInstalarNoIphone()) {
      setInstalarAberto(true);
      return;
    }
    setEstado('ligando');
    try {
      await ligarAvisos({ evento: eventoId, carteira: carteiraCodigo, papel });
      setEstado('ligado');
      mostrarToast(toastSucesso, 5000);
    } catch (e) {
      if (permissaoNegada()) {
        setEstado('bloqueado');
      } else {
        setEstado('normal');
        mostrarToast(mensagemAmigavel(e, 'Não conseguimos ligar os avisos.'), 6000);
      }
    }
  }

  async function confirmarDesligar() {
    setConfirmandoDesligar(false);
    try {
      await desligarAvisos(eventoId);
    } catch {
      /* não bloqueia a tela */
    }
    setEstado('normal');
    mostrarToast('Avisos desligados.', 3000);
  }

  if (!suportaAvisos()) return null;

  if (estado === 'bloqueado') {
    return <p className={`text-sm leading-relaxed text-foreground-600 ${className}`}>{TEXTO_BLOQUEADO}</p>;
  }

  const basePill =
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors';

  return (
    <>
      {estado === 'ligado' && confirmandoDesligar ? (
        <div className={`flex flex-wrap items-center gap-2 ${className}`}>
          <span className="text-sm text-foreground-600">Desligar avisos?</span>
          <button
            type="button"
            onClick={confirmarDesligar}
            className="cursor-pointer whitespace-nowrap rounded-full bg-danger-500 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-danger-600"
          >
            Desligar
          </button>
          <button
            type="button"
            onClick={() => setConfirmandoDesligar(false)}
            className="cursor-pointer whitespace-nowrap rounded-full border border-secondary-300 bg-white px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            Manter
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={estado === 'ligado' ? () => setConfirmandoDesligar(true) : ligar}
          disabled={estado === 'ligando' || estado === 'verificando'}
          className={`${basePill} cursor-pointer ${
            estado === 'ligado'
              ? 'bg-success-500 text-white hover:bg-success-600'
              : 'bg-primary-500 text-white hover:bg-primary-600'
          } disabled:cursor-default disabled:opacity-70 ${className}`}
        >
          {estado === 'ligando' ? (
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-loader-4-line animate-spin" />
            </span>
          ) : (
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={estado === 'ligado' ? 'ri-notification-3-fill' : 'ri-notification-3-line'} />
            </span>
          )}
          {estado === 'ligado' ? 'Avisos ligados' : rotulo}
        </button>
      )}

      {instalarAberto && (
        <FolhaColocarNaTela
          aoFechar={() => setInstalarAberto(false)}
          descricao="No iPhone, os avisos chegam quando o Se Liga está na tela do celular."
        />
      )}
    </>
  );
}